import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, ArrowRight } from 'lucide-react';
import {
  collection,
  onSnapshot,
  orderBy,
  limit,
  query,
} from 'firebase/firestore';
import { db } from '@/firebase';
import { MemberLocation, Geofence, Checkin } from '@/types';

interface LocationWidgetProps {
  householdId: string | null | undefined;
}

interface MemberEntry {
  userId: string;
  location: MemberLocation;
}

const LocationWidget: React.FC<LocationWidgetProps> = ({ householdId }) => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<MemberEntry[]>([]);
  const [geofences, setGeofences] = useState<Geofence[]>([]);
  const [lastCheckin, setLastCheckin] = useState<Checkin | null>(null);

  useEffect(() => {
    if (!householdId) return;

    const unsubs: Array<() => void> = [];

    unsubs.push(
      onSnapshot(collection(db, 'households', householdId, 'memberLocations'), (snap) => {
        const entries: MemberEntry[] = snap.docs.map((d) => ({
          userId: d.id,
          location: d.data() as MemberLocation,
        }));
        setMembers(entries);
      })
    );

    unsubs.push(
      onSnapshot(collection(db, 'households', householdId, 'geofences'), (snap) => {
        setGeofences(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Geofence, 'id'>) }))
        );
      })
    );

    const checkinsQ = query(
      collection(db, 'households', householdId, 'checkins'),
      orderBy('timestamp', 'desc'),
      limit(1)
    );
    unsubs.push(
      onSnapshot(checkinsQ, (snap) => {
        if (!snap.empty) {
          setLastCheckin({ id: snap.docs[0].id, ...(snap.docs[0].data() as Omit<Checkin, 'id'>) });
        } else {
          setLastCheckin(null);
        }
      })
    );

    return () => unsubs.forEach((u) => u());
  }, [householdId]);

  if (!householdId) {
    return (
      <div className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4">
        <p className="text-xs text-slate-400 italic">Household required</p>
      </div>
    );
  }

  const now = Date.now();
  const activeMembers = members.filter(
    (m) => now - m.location.timestamp.toMillis() < 5 * 60 * 1000
  );
  const displayMembers = members.slice(0, 3);
  const overflowCount = Math.max(0, members.length - 3);

  const checkinAgoMs = lastCheckin
    ? now - (lastCheckin as any).timestamp?.toMillis?.()
    : null;
  const checkinAgoMin = checkinAgoMs !== null ? Math.floor(checkinAgoMs / 60000) : null;

  return (
    <div
      className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-2xl p-4 cursor-pointer hover:border-brand/40 transition-colors"
      onClick={() => navigate('/family-location')}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-brand" />
          <h3 className="text-xs font-black uppercase tracking-widest text-slate-500">
            Family Location
          </h3>
        </div>
        {geofences.length > 0 && (
          <span className="text-[9px] font-black uppercase tracking-wider px-2 py-0.5 bg-brand/10 text-brand rounded-full">
            {geofences.length} zone{geofences.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {members.length === 0 ? (
        <p className="text-xs text-slate-400 italic py-1">No members sharing location</p>
      ) : (
        <div className="space-y-2">
          {/* Member list */}
          <div className="flex items-center gap-2">
            {displayMembers.map(({ userId, location }) => {
              const isActive = now - location.timestamp.toMillis() < 5 * 60 * 1000;
              return (
                <div key={userId} className="relative">
                  <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center text-brand font-bold text-xs">
                    {userId.charAt(0).toUpperCase()}
                  </div>
                  {isActive && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white dark:border-slate-900" />
                  )}
                </div>
              );
            })}
            {overflowCount > 0 && (
              <span className="text-xs text-slate-400 font-semibold">+{overflowCount} more</span>
            )}
            <span className="ml-auto text-xs text-slate-400">
              {activeMembers.length} active
            </span>
          </div>

          {/* Last check-in */}
          {lastCheckin && checkinAgoMin !== null && (
            <p className="text-xs text-slate-500">
              <span className="font-semibold text-slate-700 dark:text-slate-300">
                {lastCheckin.userName}
              </span>{' '}
              checked in at <span className="font-semibold">{lastCheckin.zoneName}</span>{' '}
              {checkinAgoMin < 1 ? 'just now' : `${checkinAgoMin}m ago`}
            </p>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
        <span className="text-[10px] text-slate-400">Privacy-first · 24h history</span>
        <ArrowRight className="w-3.5 h-3.5 text-brand" />
      </div>
    </div>
  );
};

export default LocationWidget;
